
<template>
  <div class="map-address-selector">
    <!-- Search Input with Autocomplete -->
    <v-text-field
      ref="autocompleteInput"
      v-model="searchQuery"
      label="Search for an address or click on the map"
      outlined
      dense
      prepend-icon="mdi-magnify"
      :loading="isLoading"
      @focus="initializeAutocomplete"
      class="mb-4"
      :hint="showMapHint ? 'You can also click on the map to select an address' : ''"
      persistent-hint
    ></v-text-field>

    <!-- Map Container -->
    <v-card class="map-container" elevation="2">
      <v-card-title class="py-2 px-3">
        <v-icon class="mr-2">mdi-map</v-icon>
        Select Address on Map
        <v-spacer></v-spacer>
        <v-btn
          icon
          small
          @click="getCurrentLocation"
          :loading="isGettingLocation"
          color="primary"
        >
          <v-icon>mdi-crosshairs-gps</v-icon>
        </v-btn>
      </v-card-title>
      
      <div class="map-wrapper">
        <GmapMap
          ref="mapRef"
          :center="mapCenter"
          :zoom="mapZoom"
          @click="onMapClick"
          style="height: 300px; width: 100%"
          :options="{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
            styles: mapStyles
          }"
        >
          <GmapMarker
            v-if="selectedLocation"
            :position="selectedLocation"
            :icon="markerIcon"
            :animation="2"
          />
        </GmapMap>
        
        <!-- Loading overlay -->
        <v-overlay v-if="isReverseGeocoding" absolute>
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <div class="mt-2 text-center">Getting address...</div>
        </v-overlay>
      </div>
    </v-card>

    <!-- Selected Address Display -->
    <v-card v-if="selectedAddress" class="mt-4" outlined>
      <v-card-text class="py-2">
        <div class="d-flex align-center">
          <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
          <div>
            <div class="font-weight-medium">Selected Address:</div>
            <div class="text--secondary">{{ selectedAddress.formatted_address }}</div>
          </div>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            small
            @click="confirmAddress"
            :disabled="!selectedAddress"
          >
            Use This Address
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'MapAddressSelector',
  data() {
    return {
      searchQuery: '',
      mapCenter: { lat: 39.8283, lng: -98.5795 }, // Center of USA
      mapZoom: 4,
      selectedLocation: null,
      selectedAddress: null,
      isLoading: false,
      isReverseGeocoding: false,
      isGettingLocation: false,
      autocomplete: null,
      geocoder: null,
      showMapHint: true,
      markerIcon: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22S19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9S10.62 6.5 12 6.5S14.5 7.62 14.5 9S13.38 11.5 12 11.5Z" fill="#1976d2"/>
          </svg>
        `),
        scaledSize: { width: 32, height: 32 },
        anchor: { x: 16, y: 32 }
      },
      mapStyles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        }
      ]
    }
  },
  mounted() {
    this.initializeGoogleServices();
    this.getCurrentLocation();
  },
  methods: {
    async initializeGoogleServices() {
      // Wait for Google Maps to be ready
      await this.$gmapApiPromiseLazy();
      this.geocoder = new google.maps.Geocoder();
    },

    initializeAutocomplete() {
      if (this.autocomplete || !this.$refs.autocompleteInput) return;
      
      const input = this.$refs.autocompleteInput.$refs.input;
      this.autocomplete = new google.maps.places.Autocomplete(input, {
        types: ['address'],
        componentRestrictions: { country: 'us' }
      });

      this.autocomplete.addListener('place_changed', () => {
        const place = this.autocomplete.getPlace();
        if (place.geometry) {
          this.handlePlaceSelection(place);
        }
      });
    },

    handlePlaceSelection(place) {
      this.selectedLocation = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      };
      
      this.selectedAddress = place;
      this.mapCenter = this.selectedLocation;
      this.mapZoom = 16;
      this.searchQuery = place.formatted_address;
    },

    async onMapClick(event) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      
      this.selectedLocation = { lat, lng };
      this.isReverseGeocoding = true;
      
      try {
        const address = await this.reverseGeocode(lat, lng);
        if (address) {
          this.selectedAddress = address;
          this.searchQuery = address.formatted_address;
        }
      } catch (error) {
        console.error('Reverse geocoding failed:', error);
        this.$emit('error', 'Failed to get address for selected location');
      } finally {
        this.isReverseGeocoding = false;
      }
    },

    reverseGeocode(lat, lng) {
      return new Promise((resolve, reject) => {
        this.geocoder.geocode(
          { location: { lat, lng } },
          (results, status) => {
            if (status === 'OK' && results[0]) {
              resolve(results[0]);
            } else {
              reject(new Error('Geocoding failed: ' + status));
            }
          }
        );
      });
    },

    getCurrentLocation() {
      if (!navigator.geolocation) {
        this.$emit('error', 'Geolocation is not supported by this browser');
        return;
      }

      this.isGettingLocation = true;
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.mapCenter = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          this.mapZoom = 13;
          this.isGettingLocation = false;
        },
        (error) => {
          console.error('Geolocation error:', error);
          this.isGettingLocation = false;
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000
        }
      );
    },

    confirmAddress() {
      if (!this.selectedAddress) return;

      const addressComponents = this.parseAddressComponents(this.selectedAddress.address_components);
      
      const addressData = {
        address: this.getStreetAddress(this.selectedAddress.address_components),
        city: addressComponents.city,
        state: addressComponents.state,
        zip: addressComponents.zip_code,
        formatted_address: this.selectedAddress.formatted_address,
        coordinates: this.selectedLocation
      };

      this.$emit('address-selected', addressData);
    },

    parseAddressComponents(components) {
      const result = {
        street_number: '',
        route: '',
        city: '',
        state: '',
        zip_code: '',
        country: ''
      };

      components.forEach(component => {
        const types = component.types;
        
        if (types.includes('street_number')) {
          result.street_number = component.long_name;
        } else if (types.includes('route')) {
          result.route = component.long_name;
        } else if (types.includes('locality')) {
          result.city = component.long_name;
        } else if (types.includes('administrative_area_level_1')) {
          result.state = component.short_name;
        } else if (types.includes('postal_code')) {
          result.zip_code = component.long_name;
        } else if (types.includes('country')) {
          result.country = component.long_name;
        }
      });

      return result;
    },

    getStreetAddress(components) {
      const streetNumber = components.find(c => c.types.includes('street_number'))?.long_name || '';
      const route = components.find(c => c.types.includes('route'))?.long_name || '';
      
      return `${streetNumber} ${route}`.trim();
    },

    reset() {
      this.searchQuery = '';
      this.selectedLocation = null;
      this.selectedAddress = null;
      this.mapCenter = { lat: 39.8283, lng: -98.5795 };
      this.mapZoom = 4;
    }
  }
}
</script>

<style scoped>
.map-address-selector {
  width: 100%;
}

.map-container {
  border-radius: 8px;
  overflow: hidden;
}

.map-wrapper {
  position: relative;
}

.pac-container {
  z-index: 9999 !important;
}
</style>